import { __decorate, __metadata } from "tslib";
import { Component, ModelSync, Prop, Ref, Watch } from "vue-property-decorator";
import { DateTime, Info } from "luxon";
import LvField from "../field/LvField.vue";
import LvControlPopover from "../control-popover/LvControlPopover.vue";
import { UIComponent } from "../UIComponent";
import { getLocaleDateFormat, parseLocaleDate } from "@fastoche/ui-core/i18n/date";
import { LvIcon } from "../icon";
import { LvCalendarMonth } from "../calendar-month";
const DEFAULT_START_YEAR = new Date().getFullYear() - 120;
const DEFAULT_END_YEAR = new Date().getFullYear() + 50;
const YEARS = Array(DEFAULT_END_YEAR - DEFAULT_START_YEAR + 1).fill(0).map((_, idx) => DEFAULT_START_YEAR + idx);
export const COMPONENT_NAME = "LvDatePicker";
/**
 * A component to select a date
 */
let LvDatePicker = class LvDatePicker extends UIComponent {
    constructor() {
        super(...arguments);
        this.calendarMonthDate = DateTime.now().startOf("month");
        this.input = "";
        this.localeFormat = getLocaleDateFormat();
    }
    mounted() {
        this.initialize();
    }
    initialize() {
        this.localeFormat = getLocaleDateFormat();
        this.onModelChange();
    }
    /**
     * Show the date picker popup
     */
    show() {
        this.popover.show();
    }
    /**
     * Hide the date picker popup
     */
    hide() {
        this.popover.hide();
    }
    /**
     * Display the previous month.
     * <br>This does not change the selected date.
     */
    prevMonth() {
        this.calendarMonthDate = this.calendarMonthDate.minus({ month: 1 });
    }
    /**
     * Display the next month.
     * <br>This does not change the selected date.
     */
    nextMonth() {
        this.calendarMonthDate = this.calendarMonthDate.plus({ month: 1 });
    }
    get displayPlaceholder() {
        if (this.placeholder) {
            return this.placeholder;
        }
        else {
            return this.localeFormat.placeholder;
        }
    }
    get years() {
        let years = YEARS;
        if (this.minDate) {
            const minYear = DateTime.fromISO(this.minDate).year;
            years = years.filter(x => x >= minYear);
        }
        if (this.maxDate) {
            const maxYear = DateTime.fromISO(this.maxDate).year;
            years = years.filter(x => x <= maxYear);
        }
        return years;
    }
    get months() {
        return Info.months("long");
    }
    get month() {
        return this.calendarMonthDate.monthLong;
    }
    set month(value) {
        const monthNumber = this.months.indexOf(value) + 1;
        this.calendarMonthDate = this.calendarMonthDate.set({ month: monthNumber });
    }
    get year() {
        return this.calendarMonthDate.year;
    }
    set year(value) {
        this.calendarMonthDate = this.calendarMonthDate.set({ year: value });
    }
    onModelChange() {
        const date = this.model ? DateTime.fromISO(this.model) : null;
        if (!date) {
            this.input = "";
            if (this.minDate) {
                this.calendarMonthDate = DateTime.fromISO(this.minDate).startOf("month");
            }
            else if (this.maxDate) {
                this.calendarMonthDate = DateTime.fromISO(this.maxDate).startOf("month");
            }
        }
        else {
            this.calendarMonthDate = date.startOf("month");
            this.input = date.toFormat(this.localeFormat.displayFormat);
        }
    }
    onInputChange() {
        if (!this.input) {
            this.model = null;
        }
        else {
            const date = parseLocaleDate(this.input);
            if (date && date.isValid && !this.isDayDisabled(date.toISO())) {
                this.model = date.toISO();
            }
            else {
                this.model = null;
                this.input = "";
            }
        }
    }
    isDayDisabled(day) {
        if (!this.minDate && !this.maxDate)
            return false;
        const dayDateTime = DateTime.fromISO(day);
        const minDateTime = this.minDate ? DateTime.fromISO(this.minDate) : null;
        const maxDateTime = this.maxDate ? DateTime.fromISO(this.maxDate) : null;
        return ((minDateTime && dayDateTime < minDateTime) ||
            (maxDateTime && dayDateTime > maxDateTime));
    }
};
__decorate([
    ModelSync("value", "change", { type: String, required: false, default: () => null }),
    __metadata("design:type", Object)
], LvDatePicker.prototype, "model", void 0);
__decorate([
    Prop({ type: String, required: false, default: () => null }),
    __metadata("design:type", String)
], LvDatePicker.prototype, "minDate", void 0);
__decorate([
    Prop({ type: String, required: false, default: () => null }),
    __metadata("design:type", String)
], LvDatePicker.prototype, "maxDate", void 0);
__decorate([
    Prop({ type: String, required: false, default: null }),
    __metadata("design:type", String)
], LvDatePicker.prototype, "placeholder", void 0);
__decorate([
    Ref(),
    __metadata("design:type", LvControlPopover)
], LvDatePicker.prototype, "popover", void 0);
__decorate([
    Watch("model"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], LvDatePicker.prototype, "onModelChange", null);
LvDatePicker = __decorate([
    Component({
        name: COMPONENT_NAME,
        components: {
            LvControlPopover,
            LvField,
            LvIcon,
            LvCalendarMonth,
        },
    })
], LvDatePicker);
export default LvDatePicker;
//# sourceMappingURL=script.js.map